import {HighlightRange} from "components/dashboard/product-template-table/utils/interfaces";
import {DatasetFullDefaultConfig} from "models/dataset-models";
import {Button} from "primereact/button";
import {ColorPicker} from "primereact/colorpicker";
import {Column} from "primereact/column";
import {DataTable} from "primereact/datatable";
import {InputNumber} from "primereact/inputnumber";
import {InputSwitch} from "primereact/inputswitch";
import React, {useState} from "react";

interface HighlightEditorProps {
    identifier: string;
    currentConfig: DatasetFullDefaultConfig;
    setCurrentConfig: React.Dispatch<React.SetStateAction<DatasetFullDefaultConfig>>;
    onConfigChange: any;
}

const HighlightsEditor = ({currentConfig, setCurrentConfig, onConfigChange, identifier}: HighlightEditorProps) => {

    const [highlightRanges, setHighlightRanges] = useState<HighlightRange[]>(currentConfig.datasets?.filter((dataset) => dataset.identifier === identifier)[0].highlightRanges ?? []);

    const onAddHighlightRange = () => {
        let highlightRangesToUpdate = structuredClone(highlightRanges);
        highlightRangesToUpdate.push({color: "#000000", bgColor: "#FFFFFF", min: {value: 0, inclusive: true}, max: {value: 0, inclusive: true}});
        setHighlightRanges(highlightRangesToUpdate);
    }

    const onRowEditComplete = (e: any) => {
        let datasetsToUpdate = [...currentConfig.datasets!];
        const {newData, index} = e;
        let datasetToUpdate = {...datasetsToUpdate.filter((dataset) => dataset.identifier === identifier)[0]};
        let highlightRangesToUpdate = structuredClone(highlightRanges);
        highlightRangesToUpdate[index] = newData;
        datasetToUpdate.highlightRanges = highlightRangesToUpdate;
        datasetsToUpdate[datasetsToUpdate.indexOf(datasetsToUpdate.filter((dataset) => dataset.identifier === identifier)[0])] = datasetToUpdate;
        setHighlightRanges(highlightRangesToUpdate);

        setCurrentConfig({...currentConfig, datasets: datasetsToUpdate});
        onConfigChange({...currentConfig, datasets: datasetsToUpdate});
    };

    const numberEditor = (options: any, field: string) => {
        return <InputNumber
            value={options.value.value}
            onChange={(e) => options.editorCallback({...options.rowData[field], value: e.value})}
            inputStyle={{width: "100%"}}
        />;
    };

    const switchEditor = (options: any, field: string) => {
        return <InputSwitch
            checked={options.value.inclusive}
            onChange={(e) => options.editorCallback({...options.rowData[field], inclusive: e.value})}
        />;
    }

    const colorEditor = (options: any) => {
        return <ColorPicker
            value={options.value}
            onChange={(e) => options.editorCallback("#" + e.value)}
            style={{width: "100%"}}
        />;
    }

    const deleteButtonBodyTemplate = (rowData: any) => {
        return <Button icon="pi pi-trash" className=" p-button-danger p-button-outlined" onClick={() => {
            let highlightRangesToUpdate = structuredClone(highlightRanges);
            highlightRangesToUpdate.splice(highlightRangesToUpdate.indexOf(rowData), 1);
            setHighlightRanges(highlightRangesToUpdate);
            setCurrentConfig({...currentConfig, datasets: currentConfig.datasets?.map((dataset) => {
                    if (dataset.identifier === identifier) {
                        dataset.highlightRanges = highlightRangesToUpdate;
                    }
                    return dataset;
                }
            )});
        }} />;
    }

    const tableHeader = (
        <div style={{textAlign: 'left'}}>
            <Button icon="pi pi-plus" className=" p-button-success p-mr-2" onClick={onAddHighlightRange} />
        </div>
    );

    return (<>
            <DataTable
                value={highlightRanges}
                editMode="row"
                onRowEditComplete={onRowEditComplete}
                header={tableHeader}
                tableStyle={{width: "100%"}}
            >
                <Column
                    className="p-0"
                    field={"min"}
                    header={"Min"}
                    editor={(options) => numberEditor(options, "min")}
                    body={(rowData: any) => rowData.min.value}
                />
                <Column
                    className="p-0"
                    field={"min"}
                    header={"Min inclusive"}
                    editor={(options) => switchEditor(options, "min")}
                    body={(rowData: any) => rowData.min.inclusive}
                />
                <Column
                    className="p-0"
                    field={"max"}
                    header={"Max"}
                    editor={(options) => numberEditor(options, "max")}
                    body={(rowData: any) => rowData.max.value}
                />
                <Column
                    className="p-0"
                    field={"max"}
                    header={"Max inclusive"}
                    editor={(options) => switchEditor(options, "max")}
                    body={(rowData: any) => rowData.max.inclusive}
                />
                <Column
                    className="p-0"
                    field={"color"}
                    header={"Color"}
                    editor={colorEditor}
                    body={(rowData: any) => rowData.color}
                />
                <Column
                    className="p-0"
                    field={"bgColor"}
                    header={"Background color"}
                    editor={colorEditor}
                    body={(rowData: any) => rowData.bgColor}
                />
                <Column
                    className="p-0"
                    rowEditor
                    style={{width: "10%"}}
                    headerStyle={{width: "10%"}}
                    bodyStyle={{textAlign: "center"}}
                />
                <Column
                    className="p-0"
                    body={deleteButtonBodyTemplate}
                    style={{width: "10%"}}
                    />
            </DataTable>
        </>
    );
};

export default HighlightsEditor